import { useParams, Link, useNavigate } from 'react-router-dom';
import { articles, chapterList, comics } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useEffect } from 'react';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const article = articles.find((a) => a.id === Number(id));
  const comic = article ? comics.find((c) => c.id === article.comicId) : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article || !comic) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <p className="text-foreground-500 text-sm mb-4">找不到该章节</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm transition-all duration-200 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: comic.title, href: `/category/${comic.category}` },
    { label: article.chapterTitle },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${article.chapterTitle}`,
    description: `${comic.title}漫画${article.chapterTitle}，在线免费下拉式阅读，073漫画网为您提供高清漫画阅读体验。`,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '073漫画网',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-6">
          <header className="text-center mb-8">
            <Link
              to={`/category/${comic.category}`}
              className="inline-block text-sm text-primary-600 hover:text-primary-700 font-medium mb-3 transition-colors duration-200"
            >
              {comic.title}
            </Link>
            <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              {article.chapterTitle}
            </h1>
            <div className="flex items-center justify-center gap-4 text-xs text-foreground-500">
              <span>作者：{comic.author}</span>
              <span className="w-px h-3 bg-background-300"></span>
              <span>更新：{article.publishDate}</span>
              <span className="w-px h-3 bg-background-300"></span>
              <span>{article.views.toLocaleString()} 次阅读</span>
            </div>
          </header>

          <div className="flex items-center justify-between mb-8 py-3 border-y border-background-200/70">
            {article.prevId ? (
              <Link
                to={`/article/${article.prevId}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm text-foreground-700 font-medium transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 上一话
              </Link>
            ) : (
              <span className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm text-foreground-400 whitespace-nowrap">
                <i className="ri-arrow-left-line"></i> 上一话
              </span>
            )}

            <Link
              to={`/category/${comic.category}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-list-check"></i> 目录
            </Link>

            {article.nextId ? (
              <Link
                to={`/article/${article.nextId}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm text-foreground-700 font-medium transition-all duration-200 whitespace-nowrap"
              >
                下一话 <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <span className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm text-foreground-400 whitespace-nowrap">
                下一话 <i className="ri-arrow-right-line"></i>
              </span>
            )}
          </div>

          <div className="flex flex-col items-center gap-1">
            {article.images.map((img, index) => (
              <div key={index} className="w-full">
                <img
                  src={img}
                  alt={`${comic.title} ${article.chapterTitle} 第${index + 1}页`}
                  title={`${comic.title} ${article.chapterTitle} - 073漫画网下拉式阅读`}
                  className="w-full h-auto"
                  loading={index < 3 ? undefined : 'lazy'}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-8 py-3 border-y border-background-200/70">
            {article.prevId ? (
              <Link
                to={`/article/${article.prevId}`}
                className="flex items-center gap-1.5 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 上一话
              </Link>
            ) : (
              <span className="flex items-center gap-1.5 px-6 py-3 rounded-full text-sm text-foreground-400 whitespace-nowrap">
                <i className="ri-arrow-left-line"></i> 上一话
              </span>
            )}

            {article.nextId ? (
              <Link
                to={`/article/${article.nextId}`}
                className="flex items-center gap-1.5 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-all duration-200 whitespace-nowrap"
              >
                下一话 <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <span className="flex items-center gap-1.5 px-6 py-3 rounded-full text-sm text-foreground-400 whitespace-nowrap">
                下一话 <i className="ri-arrow-right-line"></i>
              </span>
            )}
          </div>

          <div className="mt-10 p-5 rounded-lg bg-background-100 border border-background-200/70">
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">章节列表</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {chapterList.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/article/${ch.id}`}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors duration-150 ${
                    ch.id === article.id
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'hover:bg-background-200 text-foreground-700'
                  }`}
                >
                  <span className="truncate">{ch.title}</span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{ch.date}</span>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>
    </>
  );
}